import dayjs from "dayjs";

/**
 * Reguly okna czasowego deklaracji SENT.
 *
 * Wydzielone jako czyste funkcje, zeby:
 *  - dokladnie te same reguly obowiazywaly w kalendarzu (disabledDate),
 *    w walidacji formularza i przed wyslaniem zadania,
 *  - dawaly sie przetestowac bez uruchamiania przegladarki
 *    (patrz tests/smoke/sent-walidacja.test.mjs).
 *
 * Dwie reguly z wymagan:
 *   1. data poczatku NIE moze byc w przeszlosci,
 *   2. deklaracja moze trwac maksymalnie 10 dni.
 *
 * GRANULACJA DNIOWA, nie godzinowa. Wynika z kontraktu API: pola startDate
 * i endDate w SentDeclarationCreateRequest maja format "date" (YYYY-MM-DD),
 * nie "date-time". Godziny nie sa wiec czescia deklaracji.
 *
 * INTERPRETACJA "10 DNI": liczba dni WLACZNIE z dniem poczatku i dniem konca.
 * Poczatek 10 wrzesnia => najpozniejszy koniec 19 wrzesnia (10 dni).
 * To najbardziej naturalne czytanie zdania "deklaracja trwa 10 dni", ale
 * jest to interpretacja — do potwierdzenia u wlasciciela wymagania.
 */

/** Maksymalna liczba dni trwania deklaracji, wlacznie z pierwszym i ostatnim. */
export const MAX_DNI_DEKLARACJI = 10;

export type WynikWalidacji = {
    poprawne: boolean;
    /** klucz tlumaczenia bledu, np. "error_start_in_past" */
    blad?: string;
    /** liczba dni wlacznie, gdy da sie wyliczyc */
    dni?: number;
};

/**
 * Czy dany dzien jest niedostepny w kalendarzu daty POCZATKU.
 * Blokuje wszystko przed dzisiejszym dniem — samo "dzisiaj" zostaje dostepne,
 * bo przewoz moze zaczac sie tego samego dnia.
 */
export function dzienPoczatkuZablokowany(dzien: dayjs.Dayjs | null): boolean {
    if (!dzien) return false;
    return dzien.isBefore(dayjs().startOf("day"));
}

/**
 * Czy dany dzien jest niedostepny w kalendarzu daty KONCA.
 * Blokuje dni przed data poczatku oraz dni poza limitem 10 dni wlacznie.
 */
export function dzienKoncaZablokowany(
    dzien: dayjs.Dayjs | null,
    poczatek: dayjs.Dayjs | null
): boolean {
    if (!dzien) return false;
    const dolna = (poczatek ? poczatek.clone() : dayjs()).startOf("day");
    const gorna = dolna.clone().add(MAX_DNI_DEKLARACJI - 1, "days").endOf("day");
    return dzien.isBefore(dolna) || dzien.isAfter(gorna);
}

/** Liczba dni deklaracji, wlacznie z dniem poczatku i dniem konca. */
export function liczbaDni(
    poczatek: dayjs.Dayjs | null | undefined,
    koniec: dayjs.Dayjs | null | undefined
): number {
    if (!poczatek || !koniec) return 0;
    return koniec.clone().startOf("day").diff(poczatek.clone().startOf("day"), "days") + 1;
}

/**
 * Pelna walidacja okna czasowego. Zwraca pierwszy napotkany blad,
 * zeby uzytkownik nie dostawal kilku komunikatow naraz.
 */
export function sprawdzOkno(
    poczatek: dayjs.Dayjs | null | undefined,
    koniec: dayjs.Dayjs | null | undefined
): WynikWalidacji {
    if (!poczatek || !koniec) {
        return { poprawne: false, blad: "error_dates_required" };
    }

    // 1. poczatek nie w przeszlosci
    if (poczatek.clone().startOf("day").isBefore(dayjs().startOf("day"))) {
        return { poprawne: false, blad: "error_start_in_past" };
    }

    // koniec tego samego dnia jest dozwolony — przewoz jednodniowy
    if (koniec.clone().startOf("day").isBefore(poczatek.clone().startOf("day"))) {
        return { poprawne: false, blad: "error_end_before_start" };
    }

    // 2. maksymalnie 10 dni wlacznie
    const dni = liczbaDni(poczatek, koniec);
    if (dni > MAX_DNI_DEKLARACJI) {
        return { poprawne: false, blad: "error_too_long", dni };
    }

    return { poprawne: true, dni };
}

/** Liczba dni w formie czytelnej dla uzytkownika, np. "3 dni", "1 dzień". */
export function opiszLiczbeDni(dni: number): string {
    if (!dni || dni < 1) return "";
    return dni === 1 ? "1 dzień" : `${dni} dni`;
}

/** Status deklaracji wyliczany z okna czasowego i chwili obecnej. */
export type StatusDeklaracji = "oczekujaca" | "aktywna" | "zakonczona";

/**
 * Zamiana daty z API na znacznik czasu — LICZONA RAZ, przy odbiorze odpowiedzi.
 *
 * DLACZEGO TO ISTNIEJE. Sortowanie tabeli tworzylo dwa obiekty `dayjs()` przy
 * KAZDYM porownaniu, czyli okolo 1,7 miliona razy przy 50 tysiacach wierszy.
 * Pomiar: sortowanie 50 000 deklaracji zajmowalo **3682 ms**, a po przeliczeniu
 * dat z gory — **28 ms** (24 ms na przeliczenie + 4 ms na samo sortowanie).
 *
 * `Date.parse` nie rozumie formatu "2026-09-14 00:00:00" w kazdej przegladarce,
 * dlatego spacja zamieniana jest na "T", a gdyby i to zawiodlo — wracamy do
 * `dayjs()`, ktory poradzi sobie zawsze. Sciezka wolna dotyczy wtedy tylko
 * nietypowych formatow, nie kazdego wiersza.
 */
export function naZnacznik(data: string | null | undefined): number {
    if (!data) return 0;
    const szybki = Date.parse(String(data).replace(" ", "T"));
    if (!Number.isNaN(szybki)) return szybki;
    const m = dayjs(data);
    return m.isValid() ? m.valueOf() : 0;
}

/**
 * Rdzen reguly statusu — na znacznikach czasu, bez tworzenia obiektow daty.
 * Granulacja DNIOWA: deklaracja konczaca sie dzis jest jeszcze aktywna.
 *
 * To JEDYNA implementacja tej reguly; wariant tekstowy nizej tylko ja wola.
 * Dwie osobne implementacje tego samego prawidla predzej czy pozniej sie
 * rozjezdzaja, a wtedy tabela i panel pojazdow pokazuja co innego.
 */
export function statusZeZnacznikow(
    odTs: number,
    doTs: number,
    teraz: number = Date.now()
): StatusDeklaracji {
    const d = new Date(teraz);
    const poczatekDnia = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const koniecDnia = poczatekDnia + 24 * 60 * 60 * 1000 - 1;
    if (odTs > koniecDnia) return "oczekujaca";
    if (doTs < poczatekDnia) return "zakonczona";
    return "aktywna";
}

/** Wygodna nakladka dla dat tekstowych. Uzywa tego samego rdzenia. */
export function statusDeklaracji(dataOd: string, dataDo: string): StatusDeklaracji {
    return statusZeZnacznikow(naZnacznik(dataOd), naZnacznik(dataDo));
}

/** Format daty w kontrakcie API (pola startDate / endDate w zadaniu POST). */
export const FORMAT_API = "YYYY-MM-DD";
